// src/pages/imdf/detail.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOne } from "@refinedev/core";
import JSZip from 'jszip';
import LeafletMap from '../../components/map/LeafletMap';
import { usePermission } from '../../components/PermissionGuard';

interface IMDFFile {
  id: string;
  originalName: string;
  storagePath: string;
  description?: string;
  version?: string;
  category?: string;
  uploadedBy?: string;
  fileSize: number;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  createdAt: string;
  updatedAt: string;
}

interface GeoJSONFile {
  name: string;
  features: GeoJSON.Feature[];
  featureCount: number;
}

interface IMDFData {
  files: GeoJSONFile[];
  totalFeatures: number;
  fileTypes: string[];
  combinedGeoJson?: GeoJSON.FeatureCollection;
}

const IMDFDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isMaintainer } = usePermission();
  const [imdfData, setImdfData] = useState<IMDFData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // 更新功能相關狀態
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  

  const { data, isLoading, error: fetchError } = useOne<IMDFFile>({
    resource: "imdf",
    id: id!,
  });

  const fileData = data?.data;

  console.log('useOne hook state:', {
    isLoading,
    error: fetchError,
    data,
    fileData,
    id
  });

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < sizes.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${sizes[i]}`;
  };

  const getStatusClasses = (status: IMDFFile['status']): string => {
    const statusClasses: Record<IMDFFile['status'], string> = {
      valid: "bg-green-100 text-green-800",
      invalid: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      validating: "bg-blue-100 text-blue-800",
    };
    return `px-3 py-1 rounded-full text-sm font-medium ${statusClasses[status]}`;
  };

  const downloadAndProcessFile = useCallback(async () => {
    if (!fileData) return;

    console.log('Starting downloadAndProcessFile...');
    setIsProcessing(true);
    setError(null);

    try {
      // 下載檔案
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');
      
      console.log('API_URL:', API_URL);
      console.log('Request URL:', `${API_URL}/imdf/files/${id}/download`);
      
      const response = await fetch(`${API_URL}/imdf/files/${id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`檔案下載失敗: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size);
      
      // 使用 JSZip 解壓縮檔案
      const zip = new JSZip();
      const zipFile = await zip.loadAsync(blob);
      
      console.log('ZIP file loaded, contents:', Object.keys(zipFile.files));
      
      const geoJsonFiles: GeoJSONFile[] = [];
      const fileTypes: string[] = [];
      const allFeatures: GeoJSON.Feature[] = [];
      let totalFeatures = 0;

      // 處理 ZIP 檔案中的每個檔案
      for (const [fileName, file] of Object.entries(zipFile.files)) {
        if (file.dir) continue; // 跳過目錄
        
        console.log('Processing file:', fileName);
        const fileExtension = fileName.split('.').pop()?.toLowerCase();
        
        if (fileExtension === 'geojson') {
          try {
            const content = await file.async('text');
            const geoJson = JSON.parse(content);
            
            const features = geoJson.features || [];
            const featureCount = features.length;
            
            // 為每個 feature 添加檔案來源資訊
            const enhancedFeatures = features.map((feature: GeoJSON.Feature) => ({
              ...feature,
              properties: {
                ...feature.properties,
                _source_file: fileName,
                _file_type: fileName.split('.')[0],
              },
            }));
            
            geoJsonFiles.push({
              name: fileName,
              features: enhancedFeatures,
              featureCount: featureCount,
            });
            
            // 收集所有 features 用於地圖顯示
            allFeatures.push(...enhancedFeatures);
            totalFeatures += featureCount;
            
            // 收集檔案類型
            const fileType = fileName.split('.')[0];
            if (!fileTypes.includes(fileType)) {
              fileTypes.push(fileType);
            }
          } catch (err) {
            console.error(`解析 GeoJSON 檔案 ${fileName} 失敗:`, err);
          }
        }
      }

      // 創建合併的 GeoJSON FeatureCollection
      const combinedGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: allFeatures,
      };

      console.log('Final results:', {
        filesCount: geoJsonFiles.length,
        totalFeatures: totalFeatures,
        fileTypes: fileTypes,
        featuresInCombined: combinedGeoJson.features.length
      });

      setImdfData({
        files: geoJsonFiles,
        totalFeatures: totalFeatures,
        fileTypes: fileTypes,
        combinedGeoJson: combinedGeoJson,
      });

    } catch (err) {
      console.error('處理檔案時發生錯誤:', err);
      setError(err instanceof Error ? err.message : '檔案處理失敗');
    } finally {
      setIsProcessing(false);
    }
  }, [fileData, id]);

  useEffect(() => {
    console.log('fileData:', fileData);
    if (fileData) {
      console.log('File found, starting download and process regardless of status...');
      downloadAndProcessFile();
    }
  }, [fileData, downloadAndProcessFile]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 處理檔案選擇
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 檢查檔案類型
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setUpdateError('請選擇 ZIP 檔案');
        return;
      }
      
      // 檢查檔案大小（100MB 限制）
      if (file.size > 100 * 1024 * 1024) {
        setUpdateError('檔案大小不能超過 100MB');
        return;
      }
      
      setSelectedFile(file);
      setUpdateError(null);
    }
  };

  // 執行檔案更新
  const handleUpdateFile = async () => {
    if (!selectedFile || !fileData) return;

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      // 準備 FormData
      const formData = new FormData();
      formData.append('file', selectedFile);

      console.log('開始上傳檔案更新:', selectedFile.name);

      // 發送更新請求
      const response = await fetch(`${API_URL}/imdf/files/${id}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('更新回應狀態:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('更新失敗回應:', errorText);
        throw new Error(`更新失敗: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('更新成功:', result);

      // 更新成功，關閉模態視窗並重新載入數據
      setShowUpdateModal(false);
      setSelectedFile(null);
      
      // 重新獲取檔案資訊並重新處理內容
      window.location.reload(); // 簡單的重載方式
      
    } catch (err) {
      console.error('檔案更新時發生錯誤:', err);
      setUpdateError(err instanceof Error ? err.message : '更新失敗');
    } finally {
      setIsUpdating(false);
    }
  };

  // 取消更新
  const handleCancelUpdate = () => {
    setShowUpdateModal(false);
    setSelectedFile(null);
    setUpdateError(null);
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (fetchError || !fileData) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p>無法載入檔案資料</p>
        <Link to="/imdf" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          返回檔案列表
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* 導航麵包屑 */}
      <nav className="mb-6">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/imdf" className="hover:text-blue-600">
              IMDF 檔案
            </Link>
          </li>
          <li className="flex items-center">
            <span className="mx-2">/</span>
            <span className="text-gray-900">{fileData.originalName}</span>
          </li>
        </ol>
      </nav>

      {/* 檔案基本資訊 */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {fileData.originalName}
            </h1>
            <p className="text-gray-600">
              上傳時間: {new Date(fileData.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={getStatusClasses(fileData.status)}>
              {fileData.status}
            </span>
            {isMaintainer() && (
              <>
                <Link
                  to={`/imdf/edit/${id}`}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors"
                >
                  編輯檔案
                </Link>
                <button
                  onClick={() => setShowUpdateModal(true)}
                  className="px-4 py-2 bg-orange-600 text-white font-medium rounded-md hover:bg-orange-700 transition-colors"
                >
                  更新檔案
                </button>
              </>
            )}
            <button
              onClick={async () => {
                try {
                  const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
                  const token = localStorage.getItem('token');
                  
                  const response = await fetch(`${API_URL}/imdf/files/${id}/download`, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  });

                  if (!response.ok) {
                    throw new Error('下載失敗');
                  }

                  // 取得檔案名稱
                  const contentDisposition = response.headers.get('Content-Disposition');
                  let filename = fileData.originalName || 'download.zip';
                  if (contentDisposition) {
                    const matches = contentDisposition.match(/filename="(.+)"/);
                    if (matches) {
                      filename = decodeURIComponent(matches[1]);
                    }
                  }

                  // 下載檔案
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (error) {
                  console.error('下載檔案失敗:', error);
                  alert('下載檔案失敗，請稍後再試');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              下載檔案
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">版本:</span>
            <span className="ml-2 text-gray-900">{fileData.version || '未指定'}</span>
          </div>
          <div>
            <span className="text-gray-500">分類:</span>
            <span className="ml-2 text-gray-900">{fileData.category || '未指定'}</span>
          </div>
          <div>
            <span className="text-gray-500">檔案大小:</span>
            <span className="ml-2 text-gray-900">{formatFileSize(fileData.fileSize)}</span>
          </div>
        </div>

        {fileData.description && (
          <div className="mt-4">
            <span className="text-gray-500 text-sm">描述:</span>
            <p className="text-gray-900 mt-1">{fileData.description}</p>
          </div>
        )}
      </div>

      {/* 檔案內容分析 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">檔案內容分析</h2>
        
        {fileData.status !== 'valid' && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <h3 className="text-sm font-medium text-yellow-900 mb-2">檔案狀態說明</h3>
            {fileData.status === 'pending' && (
              <p className="text-yellow-700">檔案正在等待處理中，但仍嘗試顯示內容</p>
            )}
            {fileData.status === 'validating' && (
              <p className="text-yellow-700">檔案正在驗證中，但仍嘗試顯示內容</p>
            )}
            {fileData.status === 'invalid' && (
              <p className="text-yellow-700">檔案驗證失敗，但仍嘗試顯示內容</p>
            )}
          </div>
        )}

        {isProcessing && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-600">正在分析檔案內容...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {imdfData && (
          <div className="space-y-6">
            {/* 統計資訊 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-1">GeoJSON 檔案數</h3>
                <p className="text-2xl font-bold text-blue-600">{imdfData.files.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-900 mb-1">總特徵數</h3>
                <p className="text-2xl font-bold text-green-600">{imdfData.totalFeatures}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-purple-900 mb-1">檔案類型</h3>
                <p className="text-2xl font-bold text-purple-600">{imdfData.fileTypes.length}</p>
              </div>
            </div>

            {/* 地圖顯示 */}
            {imdfData.combinedGeoJson && imdfData.combinedGeoJson.features.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">IMDF 地圖視覺化</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <LeafletMap
                    geoJsonData={imdfData.combinedGeoJson}
                    height="500px"
                    className="w-full"
                  />
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <p className="flex items-center mb-2">
                    <span className="mr-2">💡</span>
                    點擊地圖上的特徵可查看詳細資訊
                  </p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2 font-medium">圖例說明:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                        <span className="text-xs">建築物</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                        <span className="text-xs">樓層</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-xs">單位</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-pink-500 rounded-full mr-2"></div>
                        <span className="text-xs">設施</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                        <span className="text-xs">開口</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                        <span className="text-xs">設備</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-cyan-500 rounded-full mr-2"></div>
                        <span className="text-xs">錨點</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                        <span className="text-xs">其他</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 檔案詳細列表 */}
            {imdfData.files.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">GeoJSON 檔案詳細資訊</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                          檔案名稱
                        </th>
                        <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                          特徵數量
                        </th>
                        <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">
                          類型
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {imdfData.files.map((file, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-900">
                            {file.name}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-900">
                            {file.featureCount}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-900">
                            {file.name.split('.')[0]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        
        {!imdfData && !isProcessing && !error && (
          <div className="text-center py-8 text-gray-500">
            <p>尚未分析檔案內容</p>
          </div>
        )}
      </div>

      {/* 更新檔案模態視窗 */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">更新 IMDF 檔案</h3>
                <p className="text-sm text-gray-600 mt-1">
                  上傳新的 ZIP 檔案來替換現有的 IMDF 資料
                </p>
              </div>
              <button
                onClick={handleCancelUpdate}
                className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="關閉"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選擇新的 IMDF ZIP 檔案
                  </label>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    僅支援 ZIP 格式，檔案大小不超過 100MB
                  </p>
                </div>

                {selectedFile && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">{selectedFile.name}</p>
                        <p className="text-xs text-blue-700">
                          大小: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {updateError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-800">{updateError}</p>
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">注意事項:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>此操作將完全替換現有的 IMDF 檔案</li>
                        <li>更新後將記錄您的操作時間和身份</li>
                        <li>建議在更新前先下載備份</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelUpdate}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleUpdateFile}
                disabled={!selectedFile || isUpdating}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    更新中...
                  </div>
                ) : (
                  '確認更新'
                )}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* 回到頂部按鈕 */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 z-50"
          aria-label="回到頂部"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default IMDFDetailPage;